/** BomTab — Project BOM + separate Small Power BOM with distributor multiplier. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download } from "@phosphor-icons/react";
import { downloadFile } from "./_shared";

export default function BomTab({ projectId, project, update }) {
  const [bom, setBom] = useState(null);
  const [spBom, setSpBom] = useState(null);
  const [mult, setMult] = useState(project?.distributor_multiplier ?? "");

  const load = async () => {
    try {
      const params = {};
      const m = Number(mult);
      if (mult !== "" && m > 0 && m <= 1) params.distributor_multiplier = m;
      const [{ data }, { data: sp }] = await Promise.all([
        api.get(`/projects/${projectId}/bom`, { params }),
        api.get(`/projects/${projectId}/bom/small-power`, { params }),
      ]);
      setBom(data); setSpBom(sp);
    } catch { toast.error("Failed to compute BOM"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, mult]);

  const persistMult = () => {
    if (!update) return;
    const m = Number(mult);
    if (mult === "" || isNaN(m)) {
      update({ distributor_multiplier: null });
      toast.info("Distributor multiplier cleared");
    } else if (m > 0 && m <= 1) {
      update({ distributor_multiplier: m });
      toast.success(`Distributor multiplier saved (×${m.toFixed(2)})`);
    } else {
      toast.error("Multiplier must be between 0 and 1 (e.g. 0.55 = 45% off list)");
    }
  };

  if (!bom) return <div className="text-gray-500 font-mono">Computing BOM…</div>;

  return (
    <div className="space-y-10">
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="label-tag">// 05 Bill of Materials</div>
            <h2 className="font-display text-2xl font-bold">Project BOM · Equipment list + catalog parts</h2>
            <p className="text-sm text-gray-400 mt-1">Aggregated from the Equipment tab&apos;s catalog parts and the Equipment List.</p>
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex flex-col">
              <label className="label-tag mb-1" htmlFor="bom-distributor-mult">Distributor multiplier</label>
              <div className="flex items-center gap-2">
                <input id="bom-distributor-mult" type="number" step="0.01" min={0} max={1}
                  value={mult} onChange={(e) => setMult(e.target.value)}
                  placeholder="e.g. 0.55"
                  className="w-24 bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="bom-distributor-mult" />
                <button onClick={persistMult} className="btn-ghost text-xs py-1.5" data-testid="bom-distributor-save">Save</button>
              </div>
              <div className="text-[10px] text-gray-500 font-mono mt-1">0 &lt; m ≤ 1. Applies to both BOMs + PDF.</div>
            </div>
            <button onClick={load} className="btn-ghost text-sm py-2" data-testid="bom-recompute">Recompute</button>
            <button
              onClick={async () => {
                try { await downloadFile(`/projects/${projectId}/bom/xlsx`, "bom.xlsx"); toast.success("BOM XLSX exported"); }
                catch { toast.error("BOM XLSX export failed"); }
              }}
              className="btn-ghost text-sm py-2 inline-flex items-center gap-2" data-testid="bom-export-xlsx">
              <Download size={16} /> Export XLSX
            </button>
          </div>
        </div>

        <div className="border border-white/10 overflow-x-auto" data-testid="bom-table">
          <table className="w-full">
            <thead className="bg-[#1A1F29]">
              <tr className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-mono text-left">
                <th className="p-3">Part #</th><th className="p-3">Manufacturer</th><th className="p-3">Category</th><th className="p-3">Description</th>
                <th className="p-3 text-right">Qty</th><th className="p-3 text-right">Unit</th><th className="p-3 text-right">Ext</th>
                {bom.distributor_multiplier && <th className="p-3 text-right text-[#FFCC00]">Net Unit</th>}
                {bom.distributor_multiplier && <th className="p-3 text-right text-[#FFCC00]">Net Ext</th>}
              </tr>
            </thead>
            <tbody>
              {bom.items.map((i, idx) => (
                <tr key={`${i.part_number || "row"}-${i.manufacturer || ""}-${idx}`} className="border-t border-white/5 font-mono text-sm">
                  <td className="p-3 text-[#00E5FF]">{i.part_number}</td>
                  <td className="p-3 capitalize">{i.manufacturer}</td>
                  <td className="p-3 capitalize text-gray-400">{i.category.replace(/_/g, " ")}</td>
                  <td className="p-3 text-gray-300">{i.description}</td>
                  <td className="p-3 text-right">{i.quantity}</td>
                  <td className="p-3 text-right">${i.unit_price.toFixed(2)}</td>
                  <td className="p-3 text-right">${(i.unit_price * i.quantity).toFixed(2)}</td>
                  {bom.distributor_multiplier && <td className="p-3 text-right text-[#FFCC00]">${(i.net_unit_price_usd || 0).toFixed(2)}</td>}
                  {bom.distributor_multiplier && <td className="p-3 text-right text-[#FFCC00]">${(i.net_ext_price_usd || 0).toFixed(2)}</td>}
                </tr>
              ))}
              {bom.items.length === 0 && (<tr><td colSpan={bom.distributor_multiplier ? 9 : 7} className="p-6 text-center text-gray-500 text-sm">No items — add circuits or equipment.</td></tr>)}
            </tbody>
            <tfoot>
              <tr className="bg-[#00E5FF] text-black font-display font-bold">
                <td className="p-3" colSpan={6}>PROJECT TOTAL</td>
                <td className="p-3 text-right">${bom.total.toFixed(2)}</td>
                {bom.distributor_multiplier && <td className="p-3 text-right">NET ×{bom.distributor_multiplier}</td>}
                {bom.distributor_multiplier && <td className="p-3 text-right">${(bom.net_total || 0).toFixed(2)}</td>}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div>
        <div className="label-tag mb-2">// Small Power BOM</div>
        <h2 className="font-display text-2xl font-bold">Small power · receptacles · lighting</h2>
        <p className="text-sm text-gray-400 mt-1 mb-4">Auto-generated from the Small Power tab (NEC 210.52 / CEC 26-712). Tracked separately so contractors can quote it under a different scope.</p>
        <div className="border border-white/10 overflow-x-auto" data-testid="small-power-bom-table">
          <table className="w-full">
            <thead className="bg-[#1A1F29]">
              <tr className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-mono text-left">
                <th className="p-3">Part #</th><th className="p-3">Category</th><th className="p-3">Description</th>
                <th className="p-3 text-right">Qty</th><th className="p-3 text-right">Unit</th><th className="p-3 text-right">Ext</th>
                {spBom?.distributor_multiplier && <th className="p-3 text-right text-[#FFCC00]">Net Unit</th>}
                {spBom?.distributor_multiplier && <th className="p-3 text-right text-[#FFCC00]">Net Ext</th>}
              </tr>
            </thead>
            <tbody>
              {(spBom?.items || []).map((i, idx) => (
                <tr key={`sp-${i.part_number}-${idx}`} className="border-t border-white/5 font-mono text-sm">
                  <td className="p-3 text-[#FFCC00]">{i.part_number}</td>
                  <td className="p-3 capitalize text-gray-400">{i.category.replace(/_/g, " ")}</td>
                  <td className="p-3 text-gray-300">{i.description}</td>
                  <td className="p-3 text-right">{i.quantity}</td>
                  <td className="p-3 text-right">${i.unit_price.toFixed(2)}</td>
                  <td className="p-3 text-right">${(i.unit_price * i.quantity).toFixed(2)}</td>
                  {spBom?.distributor_multiplier && <td className="p-3 text-right text-[#FFCC00]">${(i.net_unit_price_usd || 0).toFixed(2)}</td>}
                  {spBom?.distributor_multiplier && <td className="p-3 text-right text-[#FFCC00]">${(i.net_ext_price_usd || 0).toFixed(2)}</td>}
                </tr>
              ))}
              {(spBom?.items || []).length === 0 && (
                <tr><td colSpan={spBom?.distributor_multiplier ? 8 : 6} className="p-6 text-center text-gray-500 text-sm">
                  No small-power items — visit the Small Power tab and Auto-install.
                </td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-[#FFCC00] text-black font-display font-bold">
                <td className="p-3" colSpan={5}>SMALL POWER TOTAL</td>
                <td className="p-3 text-right">${(spBom?.total || 0).toFixed(2)}</td>
                {spBom?.distributor_multiplier && <td className="p-3 text-right">NET ×{spBom.distributor_multiplier}</td>}
                {spBom?.distributor_multiplier && <td className="p-3 text-right">${(spBom?.net_total || 0).toFixed(2)}</td>}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
